import { Phone, Globe, Shield } from "lucide-react";

// NOTE: the inline "Salva in Rubrica" + "Apri su Mobile" buttons + the
// fold-out QR were removed on 04/06/2026 (req. A. Zorzutti — Stand-up
// pt. 4: "Eliminare pulsanti doppi"). They will be replaced by two
// dedicated QR codes (wallet add + ops-center vCard) shown inline in
// Fase 4 of the Zorzutti feedback rollout, with matching SMS short
// links for the SMS delivery channel.

export const DigitalCard = ({ operationCenter, walletUrls, customerId, translations, uiSocLabel }) => {
  const phone = operationCenter?.phone || walletUrls?.operation_center?.phone || '+39 02 36 58 9333';
  // Req. Fabrizio 11/06/2026 — the "Centrale Operativa 24/7" caption on
  // the digital card must follow the customer's UI-language choice, NOT
  // the operating-centre's country language. Without this swap, an
  // Italian-speaking customer who picked the French SOC would see
  // "Centrale Opérationnelle 24/7" on their card, which conflicts with
  // the rest of the page being in Italian. Prefer the UI-localized
  // translation (`uiSocLabel` passed from `tForm.soc_label_24_7`) when
  // present; fall back to backend label / English default.
  const fallbackLabel = uiSocLabel || translations?.soc_label_24_7 || '24/7 Security Operations Center';
  const label = uiSocLabel || operationCenter?.label || walletUrls?.operation_center?.label || fallbackLabel;
  const activeLabel = translations?.card_active_status || 'Active';

  return (
    <div className="digital-card-wrapper mb-12 animate-fade-in" style={{ animationDelay: "0.1s" }}>
      <div className="digital-card-preview" data-testid="digital-card-preview">
        {/* Tech grid overlay */}
        <div className="card-grid-overlay" />
        
        {/* Red accent stripe top */}
        <div className="absolute top-0 left-0 right-0 h-[3px] z-20" style={{ background: 'linear-gradient(90deg, transparent 0%, #E31837 30%, #E31837 70%, transparent 100%)' }} />

        {/* Top: Logo - large and prominent */}
        <div className="relative z-20 px-7 pt-6 flex items-center justify-between">
          <div className="flex items-center gap-4" data-testid="card-logo">
            <img 
              src="/lojack_logo_white.png" 
              alt="LoJack Tracker" 
              className="h-14"
              style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.4))' }}
            />
          </div>
          <Shield className="w-6 h-6 text-lojack-red opacity-60" strokeWidth={1.5} />
        </div>

        {/* Center: Phone - the hero element */}
        <div className="relative z-20 px-7 flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full border border-lojack-red/40 flex items-center justify-center bg-lojack-red/10">
              <Phone className="w-5 h-5 text-lojack-red" strokeWidth={2} />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-gray-400 font-medium">{label}</span>
          </div>
          <a 
            href={`tel:${phone}`} 
            className="block text-lojack-red text-3xl font-bold tracking-wider ml-0 hover:opacity-80 transition-opacity"
            style={{ textShadow: '0 0 20px rgba(227,24,55,0.3)' }}
            data-testid="card-phone-number"
          >
            {phone}
          </a>
        </div>

        {/* Bottom: Website + accent line */}
        <div className="relative z-20 px-7 pb-5">
          <div className="border-t border-gray-700/50 pt-3 flex items-center justify-between">
            <a 
              href="https://lojack.eu/lojacktrackerpassive/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 text-sm hover:text-white transition-colors"
              data-testid="card-website-link"
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} />
              lojack.eu/lojacktrackerpassive
            </a>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-lojack-blue-500 animate-pulse" />
              <span className="text-xs text-lojack-blue-500/80 font-medium uppercase tracking-wider">{activeLabel}</span>
            </div>
          </div>
        </div>

        {/* Red accent stripe bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] z-20" style={{ background: 'linear-gradient(90deg, transparent 0%, #E31837 30%, #E31837 70%, transparent 100%)' }} />
      </div>

      {/* ── Utility row removed (req. A. Zorzutti — Stand-up 04/06/2026
          pt. 4): the "Salva in Rubrica" + "Apri su Mobile" buttons
          have been retired in favour of the dedicated QR codes that
          will be added inline (Fase 4 of Zorzutti feedback). vCard
          download is still reachable from the contract-signed email. */}
    </div>
  );
};
